import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { DateTime } from 'luxon';
import { Model } from '@shiftly/shared/src/schema';

export interface MiscModelSplitsData {
  all_models?: { 1: number; 2: number };
  uk?: { 1: number; 2: number };
  fp?: { 1: number; 2: number };
  gg_swap?: { 1: number; 2: number };
}

export interface MiscModelSplit {
  id: string;
  date: string;
  data: MiscModelSplitsData;
  created_at: string;
  updated_at: string;
}

export function generateModelSplitsData(models: Model[]): MiscModelSplitsData {
  const activeModels = models.filter(m => m.account_type === 'Normal' && !m.paused);

  const allSet1 = activeModels.filter(m => m.set === 1).length;
  const allSet2 = activeModels.filter(m => m.set === 2).length;

  const ukModels = activeModels.filter(m => m.pay_class === 'UK');
  const ukSet1 = ukModels.filter(m => m.set === 1).length;
  const ukSet2 = ukModels.filter(m => m.set === 2).length;

  const fpModels = activeModels.filter(m => m.pay_class === 'FP');
  const fpSet1 = fpModels.filter(m => m.set === 1).length;
  const fpSet2 = fpModels.filter(m => m.set === 2).length;

  const ggSwapModels = activeModels.filter(m => m.groups?.includes('GG Swap'));
  const ggSwapSet1 = ggSwapModels.filter(m => m.set === 1).length;
  const ggSwapSet2 = ggSwapModels.filter(m => m.set === 2).length;

  return {
    all_models: { 1: allSet1, 2: allSet2 },
    uk: { 1: ukSet1, 2: ukSet2 },
    fp: { 1: fpSet1, 2: fpSet2 },
    gg_swap: { 1: ggSwapSet1, 2: ggSwapSet2 }
  };
}

const SupabaseMiscModelSplitsService = {
  async getMiscModelSplits(month: DateTime): Promise<MiscModelSplit | null> {
    try {
      const monthStart = month.startOf('month').toISODate();

      const { data, error } = await supabase
        .from('misc_model_splits')
        .select('*')
        .eq('date', monthStart)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching misc model splits:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching misc model splits:', error);
      return null;
    }
  },

  async upsertMiscModelSplits(month: DateTime, data: MiscModelSplitsData): Promise<void> {
    const monthStart = month.startOf('month').toISODate();

    const { error } = await supabase
      .from('misc_model_splits')
      .upsert({
        date: monthStart,
        data
      }, {
        onConflict: 'date'
      });

    if (error) throw error;
  }
};

export const useMiscModelSplits = (month: DateTime) => {
  return useQuery({
    queryKey: ['supabase-miscModelSplits', month.toISO()],
    queryFn: () => SupabaseMiscModelSplitsService.getMiscModelSplits(month),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpsertMiscModelSplits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ month, data }: { month: DateTime; data: MiscModelSplitsData }) =>
      SupabaseMiscModelSplitsService.upsertMiscModelSplits(month, data),
    onSuccess: (_, variables) => {
      const queryKey = ['supabase-miscModelSplits', variables.month.toISO()];
      queryClient.invalidateQueries({ queryKey });
    }
  });
};

export default SupabaseMiscModelSplitsService;
